// Organization, venue and pickup point rules: the five-step setup, the settings page, opening hours, names,
// language subsets, the Google Place ID and the feedback switches. Pure functions, no I/O. The API validates
// request bodies with these and the web app validates each form with the same functions, so both sides refuse
// the same inputs.
import { UI } from './copy';
import { messageSample, parseMessagesPatch, type MessageOverrideError, type MessagesPatch } from './messages';
import { googleReviewUrl, type OverrideKind } from './templates';
import { parsePosterLang2 } from './poster';
import { SUPPORTED_LANGS, type Lang, type NumberMode } from './ticket';

// ---- the owner's first setup ----

/** The five steps of the first setup, in order (user flows: "owner 5 steps once"). */
export const SETUP_STEPS = ['organization', 'venue', 'hours', 'mode', 'languages'] as const;
export type SetupStep = (typeof SETUP_STEPS)[number];

/** Names typed by owners: organization, venue, city, pickup point. Whitespace is collapsed. */
export const NAME_MAX = 80;

/** A trimmed name of 1 to `max` characters, with runs of whitespace collapsed; null when empty or too long. */
export function cleanName(input: unknown, max: number = NAME_MAX): string | null {
  if (typeof input !== 'string') return null;
  const s = input.replace(/\s+/g, ' ').trim();
  if (s.length === 0 || s.length > max) return null;
  return s;
}

// ---- opening hours ----

/** JavaScript weekday: 0 is Sunday. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Display order of the week, Monday first. */
export const WEEK_ORDER: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 0];

/** One opening range in 24-hour "HH:MM". A `close` at or before `open` ends after midnight (e.g. 18:00 to 01:00). */
export interface HoursRange {
  open: string;
  close: string;
}

/** Ranges per weekday; an empty array (or a missing day) is a closed day. */
export type OpeningHours = Record<Weekday, HoursRange[]>;

/** At most this many ranges per day (a lunch and a dinner service, plus one). */
export const MAX_RANGES_PER_DAY = 3;

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Minutes since midnight for "HH:MM", or null when malformed. */
export function parseTime(s: unknown): number | null {
  if (typeof s !== 'string') return null;
  const m = TIME_RE.exec(s);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** The pre-filled schedule of the setup: open every day from 11:00 to 23:00. */
export function defaultOpeningHours(): OpeningHours {
  const h = {} as OpeningHours;
  for (const d of WEEK_ORDER) h[d] = [{ open: '11:00', close: '23:00' }];
  return h;
}

/** A schedule with every day closed. */
export function closedAllWeek(): OpeningHours {
  const h = {} as OpeningHours;
  for (const d of WEEK_ORDER) h[d] = [];
  return h;
}

/**
 * Validates a schedule typed by an owner or received by the API. Accepts an object keyed by weekday (0 to 6, as
 * numbers or digit strings); each value is an array of ranges with valid times, open different from close, at
 * most three ranges, and no two ranges of a day overlapping. Missing days are closed. Null when anything is off.
 */
export function parseOpeningHours(input: unknown): OpeningHours | null {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) return null;
  const obj = input as Record<string, unknown>;
  for (const k of Object.keys(obj)) if (!/^[0-6]$/.test(k)) return null;
  const out = closedAllWeek();
  for (const d of WEEK_ORDER) {
    const raw = obj[String(d)];
    if (raw === undefined) continue;
    if (!Array.isArray(raw) || raw.length > MAX_RANGES_PER_DAY) return null;
    const ranges: HoursRange[] = [];
    for (const r of raw) {
      if (typeof r !== 'object' || r === null) return null;
      const { open, close } = r as { open?: unknown; close?: unknown };
      const o = parseTime(open);
      const c = parseTime(close);
      if (o === null || c === null || o === c) return null;
      ranges.push({ open: open as string, close: close as string });
    }
    ranges.sort((a, b) => parseTime(a.open)! - parseTime(b.open)!);
    for (let i = 1; i < ranges.length; i++) {
      const prev = ranges[i - 1];
      const prevClose = parseTime(prev.close)!;
      const prevOpen = parseTime(prev.open)!;
      // a range that runs past midnight must be the last one of the day
      if (prevClose <= prevOpen || parseTime(ranges[i].open)! < prevClose) return null;
    }
    out[d] = ranges;
  }
  return out;
}

/** The stored form (a JSON text column). */
export function serializeOpeningHours(h: OpeningHours): string {
  const compact: Record<string, HoursRange[]> = {};
  for (const d of WEEK_ORDER) if (h[d].length > 0) compact[String(d)] = h[d];
  return JSON.stringify(compact);
}

/** Reads the stored column; null when nothing is stored or the text is not a valid schedule. */
export function openingHoursFromJson(text: string | null | undefined): OpeningHours | null {
  if (!text) return null;
  try {
    return parseOpeningHours(JSON.parse(text));
  } catch {
    return null;
  }
}

/** True when at least one day has a range. */
export function hasOpeningHours(h: OpeningHours | null): boolean {
  return h !== null && WEEK_ORDER.some((d) => h[d].length > 0);
}

/** Local weekday and minutes since midnight of an instant in a time zone. */
export function localClock(now: Date, timeZone: string = 'Africa/Casablanca'): { weekday: Weekday; minutes: number } {
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(now)) parts[p.type] = p.value;
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weekday = days.indexOf(parts.weekday) as Weekday;
  const hour = Number(parts.hour) % 24; // some engines print 24 for midnight
  return { weekday, minutes: hour * 60 + Number(parts.minute) };
}

/**
 * Whether the venue is open at `now`. No schedule (null) means always open, so a venue that never set hours is
 * never closed by mistake. A range that runs past midnight counts for the evening of its day and the early
 * hours of the next one.
 */
export function isOpenAt(h: OpeningHours | null, now: Date, timeZone: string = 'Africa/Casablanca'): boolean {
  if (h === null) return true;
  const { weekday, minutes } = localClock(now, timeZone);
  for (const r of h[weekday]) {
    const o = parseTime(r.open)!;
    const c = parseTime(r.close)!;
    if (c > o ? minutes >= o && minutes < c : minutes >= o) return true;
  }
  const yesterday = ((weekday + 6) % 7) as Weekday;
  for (const r of h[yesterday]) {
    const o = parseTime(r.open)!;
    const c = parseTime(r.close)!;
    if (c <= o && minutes < c) return true;
  }
  return false;
}

// ---- languages and number mode ----

/**
 * The customer-page languages chosen by the owner: a non-empty subset of the supported languages, in the
 * canonical order (fr, ar, en), duplicates dropped. Accepts an array or a comma-separated string. Null when
 * empty or when a value is not a supported language.
 */
export function parseLanguages(input: unknown): Lang[] | null {
  const raw = typeof input === 'string' ? input.split(',') : Array.isArray(input) ? input : null;
  if (!raw) return null;
  const set = new Set<string>();
  for (const v of raw) {
    if (typeof v !== 'string') return null;
    const l = v.trim().toLowerCase();
    if (l === '') continue;
    if (!(SUPPORTED_LANGS as readonly string[]).includes(l)) return null;
    set.add(l);
  }
  const out = SUPPORTED_LANGS.filter((l) => set.has(l));
  return out.length > 0 ? out : null;
}

/** The default language must be one of the chosen languages; otherwise the first chosen one. */
export function pickDefaultLang(languages: readonly Lang[], wanted: unknown): Lang {
  return typeof wanted === 'string' && (languages as readonly string[]).includes(wanted) ? (wanted as Lang) : languages[0];
}

export function parseNumberMode(input: unknown): NumberMode | null {
  return input === 'scan' || input === 'ticket' ? input : null;
}

// ---- the venue as the setup and the "add a venue" form send it ----

export interface VenueSetup {
  name: string;
  city: string;
  openingHours: OpeningHours;
  numberMode: NumberMode;
  languages: Lang[];
  defaultLang: Lang;
}

export type VenueSetupError = 'bad_venue_name' | 'bad_city' | 'bad_hours' | 'bad_mode' | 'bad_languages';

/**
 * Validates the venue part of a setup body: `{ name, city, opening_hours, number_mode, languages, default_lang }`.
 * The first problem found is returned as a code the UI turns into copy; nothing is partially accepted.
 */
export function parseVenueSetup(body: unknown): { value: VenueSetup; error?: undefined } | { value?: undefined; error: VenueSetupError } {
  const b = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const name = cleanName(b.name);
  if (!name) return { error: 'bad_venue_name' };
  const city = cleanName(b.city);
  if (!city) return { error: 'bad_city' };
  const openingHours = parseOpeningHours(b.opening_hours);
  if (!openingHours || !hasOpeningHours(openingHours)) return { error: 'bad_hours' };
  const numberMode = parseNumberMode(b.number_mode);
  if (!numberMode) return { error: 'bad_mode' };
  const languages = parseLanguages(b.languages);
  if (!languages) return { error: 'bad_languages' };
  return { value: { name, city, openingHours, numberMode, languages, defaultLang: pickDefaultLang(languages, b.default_lang) } };
}

// ---- pickup points ----

/** A pickup point that is archived takes no new orders; open tickets finish and the board keeps showing them. */
export function pickupPointAcceptsOrders(pp: { archived_at: number | null }): boolean {
  return pp.archived_at === null;
}

/** The last pickup point still taking orders cannot be archived: the venue's QR must land somewhere. */
export function canArchivePickupPoint(activeCount: number): boolean {
  return activeCount > 1;
}

// ---- today's hours, for the closed page ----

/** The ranges of the local weekday at `now`; empty for a closed day or when no schedule is set. */
export function todayRanges(h: OpeningHours | null, now: Date, timeZone: string = 'Africa/Casablanca'): HoursRange[] {
  if (h === null) return [];
  return h[localClock(now, timeZone).weekday];
}

/** "11:00–23:00", several ranges joined with a comma, or the closed label. Times are digits: callers show them as an LTR run. */
export function formatRanges(lang: Lang, ranges: readonly HoursRange[]): string {
  if (ranges.length === 0) return UI[lang].closed_day;
  return ranges.map((r) => `${r.open}–${r.close}`).join(', ');
}

/**
 * What the customer page says under "closed" outside the opening hours: today's ranges when the day has some
 * ("Horaires d'aujourd'hui :" plus the formatted ranges), or the closed-today line when the whole day is closed.
 * `hours` is null in the second case; when set it is digits only and goes in a `<bdi dir="ltr">`.
 */
export function todayHoursCopy(lang: Lang, h: OpeningHours | null, now: Date, timeZone: string = 'Africa/Casablanca'): { label: string; hours: string | null } {
  const ranges = todayRanges(h, now, timeZone);
  if (ranges.length === 0) return { label: UI[lang].closed_today, hours: null };
  return { label: UI[lang].today_hours, hours: formatRanges(lang, ranges) };
}

// ---- Google Place ID ----

/** Place IDs are URL-safe base64 runs (ChIJN1t_tDeuEmsRUsoyG83frY4); Google documents no fixed length. */
const PLACE_ID_RE = /^[A-Za-z0-9_-]{10,255}$/;
/** The id inside a pasted Google link: `?placeid=…` (the write-a-review link) or `place_id:…` / `place_id=…` (Maps). */
const PLACE_ID_IN_URL = /place_?id[=:]([A-Za-z0-9_-]+)/i;

/**
 * A Google Place ID typed or pasted by an owner: the id itself, or a Google link carrying it. An empty value clears
 * the id (`{ value: null }`); anything else that is not a plausible id is `bad_place_id`.
 */
export function parsePlaceId(input: unknown): { value: string | null; error?: undefined } | { value?: undefined; error: 'bad_place_id' } {
  if (input === null || input === undefined) return { value: null };
  if (typeof input !== 'string') return { error: 'bad_place_id' };
  const s = input.trim();
  if (s === '') return { value: null };
  const fromUrl = PLACE_ID_IN_URL.exec(s);
  const candidate = fromUrl ? fromUrl[1] : s;
  return PLACE_ID_RE.test(candidate) ? { value: candidate } : { error: 'bad_place_id' };
}

// ---- the feedback module and the Google link (US-50) ----

/**
 * Whether the feedback question goes out and which review link the follow-up carries. Read at send time from the
 * venue row, so a switch applies to every ticket that has not been asked yet. The Google link needs the switch and
 * a Place ID; without the id the follow-up still offers the private note (no review gating, no broken link).
 */
export function feedbackOffer(v: { feedbackEnabled: boolean; googleLinkEnabled: boolean; googlePlaceId: string | null }): { ask: boolean; reviewUrl: string | null } {
  return { ask: v.feedbackEnabled, reviewUrl: v.googleLinkEnabled && v.googlePlaceId ? googleReviewUrl(v.googlePlaceId) : null };
}

// ---- the settings page (issue #11) ----

/** The fields the settings page and `PATCH /account/venues/:venue` may change; every one is optional. */
export interface VenueSettingsPatch {
  name?: string;
  city?: string;
  numberMode?: NumberMode;
  languages?: Lang[];
  defaultLang?: Lang;
  openingHours?: OpeningHours;
  registrationsPaused?: boolean;
  googlePlaceId?: string | null;
  feedbackEnabled?: boolean;
  googleLinkEnabled?: boolean;
  /** The venue's own registration and ready texts per language (issue #20): a text, or null for the default. */
  messages?: MessagesPatch;
  /** The poster's secondary language (issue #13); null for automatic. */
  posterLang2?: Lang | null;
}

export type VenueSettingsError = VenueSetupError | 'bad_default_lang' | 'bad_place_id' | 'bad_setting' | 'bad_poster_lang' | MessageOverrideError;

/** Which text a `message_*` refusal is about, so the page can point at the field. */
export interface MessageErrorField {
  lang?: Lang;
  kind?: OverrideKind;
  placeholder?: string;
}

/** The request keys of the settings body, in the order they are checked. */
export const VENUE_SETTINGS_KEYS = ['name', 'city', 'number_mode', 'languages', 'default_lang', 'opening_hours', 'registrations_paused', 'google_place_id', 'feedback_enabled', 'google_link_enabled', 'messages', 'poster_lang_2'] as const;

/** What the settings check needs to know about the venue as it is now. */
export interface VenueSettingsCurrent {
  languages: readonly Lang[];
  defaultLang: Lang;
  /** For counting the message texts (issue #20); the name and mode in the body win when they change too. */
  name?: string;
  numberMode?: NumberMode;
  pickupPointNames?: readonly string[];
}

function parseSwitch(input: unknown): boolean | null {
  if (typeof input === 'boolean') return input;
  if (input === 1 || input === 0) return input === 1;
  return null;
}

/**
 * Validates a settings body: `{ name?, city?, number_mode?, languages?, default_lang?, opening_hours?,
 * registrations_paused?, google_place_id?, feedback_enabled?, google_link_enabled?, messages?, poster_lang_2? }`.
 * Only the keys present are checked and returned; the first problem is a code the UI turns into copy, and nothing
 * is partially accepted. The default language must belong to the languages after the change (`bad_default_lang`);
 * when the languages change and the current default is dropped, the default moves to the first chosen language. A
 * schedule must keep at least one open day, as in the setup. `messages` (issue #20) is checked with
 * `parseMessagesPatch`, each text counted as a guest would receive it with the venue name and mode after the change;
 * a refusal names the text in `field`. The poster's secondary language must be one of the languages after the
 * change and not the default (`bad_poster_lang`); null or "" means automatic (issue #13).
 */
export function parseVenueSettings(
  body: unknown,
  current: VenueSettingsCurrent,
): { value: VenueSettingsPatch; error?: undefined; field?: undefined } | { value?: undefined; error: VenueSettingsError; field?: MessageErrorField } {
  const b = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const out: VenueSettingsPatch = {};
  if (b.name !== undefined) {
    const name = cleanName(b.name);
    if (!name) return { error: 'bad_venue_name' };
    out.name = name;
  }
  if (b.city !== undefined) {
    const city = cleanName(b.city);
    if (!city) return { error: 'bad_city' };
    out.city = city;
  }
  if (b.number_mode !== undefined) {
    const mode = parseNumberMode(b.number_mode);
    if (!mode) return { error: 'bad_mode' };
    out.numberMode = mode;
  }
  let languages: readonly Lang[] = current.languages;
  if (b.languages !== undefined) {
    const parsed = parseLanguages(b.languages);
    if (!parsed) return { error: 'bad_languages' };
    languages = parsed;
    out.languages = parsed;
  }
  if (b.default_lang !== undefined) {
    if (typeof b.default_lang !== 'string' || !(languages as readonly string[]).includes(b.default_lang)) return { error: 'bad_default_lang' };
    out.defaultLang = b.default_lang as Lang;
  } else if (out.languages && !languages.includes(current.defaultLang)) {
    out.defaultLang = languages[0];
  }
  if (b.opening_hours !== undefined) {
    const hours = parseOpeningHours(b.opening_hours);
    if (!hours || !hasOpeningHours(hours)) return { error: 'bad_hours' };
    out.openingHours = hours;
  }
  if (b.registrations_paused !== undefined) {
    const v = parseSwitch(b.registrations_paused);
    if (v === null) return { error: 'bad_setting' };
    out.registrationsPaused = v;
  }
  if (b.google_place_id !== undefined) {
    const parsed = parsePlaceId(b.google_place_id);
    if (parsed.error) return { error: parsed.error };
    out.googlePlaceId = parsed.value;
  }
  if (b.feedback_enabled !== undefined) {
    const v = parseSwitch(b.feedback_enabled);
    if (v === null) return { error: 'bad_setting' };
    out.feedbackEnabled = v;
  }
  if (b.google_link_enabled !== undefined) {
    const v = parseSwitch(b.google_link_enabled);
    if (v === null) return { error: 'bad_setting' };
    out.googleLinkEnabled = v;
  }
  if (b.messages !== undefined) {
    const sample = messageSample(out.name ?? current.name ?? '', current.pickupPointNames ?? [], out.numberMode ?? current.numberMode ?? 'scan');
    const parsed = parseMessagesPatch(b.messages, sample);
    if (parsed.error !== undefined) return { error: parsed.error, field: { lang: parsed.lang, kind: parsed.kind, placeholder: parsed.placeholder } };
    out.messages = parsed.value;
  }
  if (b.poster_lang_2 !== undefined) {
    const parsed = parsePosterLang2(b.poster_lang_2, { languages, defaultLang: out.defaultLang ?? current.defaultLang });
    if (parsed.error) return { error: parsed.error };
    out.posterLang2 = parsed.value;
  }
  return { value: out };
}
